import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useLevelGenerator } from "../api/levelPacks";
import { useTracks } from "../api/queries";
import { useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, CategoryTag, Dialog, EmptyState, ErrorState, Loading, PageHeader } from "../components/ui";
import { fmtInt } from "../lib/format";
import { LevelPackForm } from "./levelpacks/LevelPackForm";
import l from "./levelpacks/levelpacks.module.css";
import { StudioError } from "./studio/components";
import styles from "./pages.module.css";

export function LevelsPage() {
  const q = useTracks();
  const canEdit = useCan("studio.edit");
  const [generating, setGenerating] = useState<string | null>(null);
  const header = (
    <PageHeader
      title="Levels"
      meta={<span>Endless tracks, delivered as immutable 50-level packs. Generate the next pack from the engine, review the draft, then publish it.</span>}
      actions={
        canEdit ? (
          <Button variant="primary" onClick={() => setGenerating("")} data-testid="generate-any">
            Generate packs
          </Button>
        ) : undefined
      }
    />
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={9} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const tracks = q.data.tracks;
  if (tracks.length === 0)
    return (
      <>
        {header}
        <EmptyState title="No tracks yet">Generate level packs with the content pipeline, then publish the manifest.</EmptyState>
      </>
    );

  const maxPlayers = Math.max(1, ...tracks.map((t) => t.players));
  return (
    <>
      {header}
      <div className={tableStyles.wrap}>
        <table className={tableStyles.table}>
          <thead>
            <tr>
              <th>Track</th>
              <th data-align="right">Players</th>
              <th style={{ width: "18%" }} aria-label="Players bar" />
              <th data-align="right">Published</th>
              <th data-align="right">Max level</th>
              <th style={{ width: "16%" }}>Reach</th>
              <th>Packs</th>
              {canEdit ? <th aria-label="Generate" /> : null}
            </tr>
          </thead>
          <tbody>
            {tracks.map((t) => {
              const available = t.packs.at(-1)?.last ?? 0;
              return (
                <tr key={t.id}>
                  <td>
                    <span className={styles.trackName}>
                      <CategoryTag category={t.id} />
                      {t.title.toLowerCase() !== t.id ? <span className={tableStyles.faint}>{t.title}</span> : null}
                    </span>
                  </td>
                  <td data-align="right" className="mono">
                    {fmtInt(t.players)}
                  </td>
                  <td>
                    <span className={styles.meter}>
                      <span style={{ width: `${(t.players / maxPlayers) * 100}%` }} />
                    </span>
                  </td>
                  <td data-align="right" className="mono">
                    {fmtInt(available)}
                  </td>
                  <td data-align="right" className="mono">
                    L{fmtInt(t.maxLevelReached)}
                  </td>
                  <td>
                    <span className={styles.reach} title={`${t.maxLevelReached} of ${available} generated levels`}>
                      <span className={styles.meter} data-tone="accent">
                        <span style={{ width: `${available ? Math.min(100, (t.maxLevelReached / available) * 100) : 0}%` }} />
                      </span>
                      <span className="mono">
                        {fmtInt(available - t.maxLevelReached)} ahead
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className={styles.packLinks}>
                      {t.packs.map((p) => (
                        <Link key={p.index} to={`/levels/${t.id}/${p.index}`} className={styles.packLink} title={`${p.chapter} · levels ${p.first}–${p.last}${p.source === "studio" ? " · published from the Studio" : ""}`}>
                          <span className="mono">{p.index}</span>
                          <span>{p.chapter}</span>
                          {p.source === "studio" ? <span className={l.studioTag}>studio</span> : null}
                        </Link>
                      ))}
                      {(t.drafts ?? []).map((d) => (
                        <Link
                          key={`d${d.index}`}
                          to={`/levels/${t.id}/${d.index}/draft`}
                          className={l.draftLink}
                          data-stale={d.stale || undefined}
                          data-testid="draft-link"
                          title={`Draft · ${d.chapter} · levels ${d.first}–${d.last}${d.verified ? ` · ${d.verified.ok}/${d.verified.checked} solver-verified` : ""}${d.stale ? " · already published: delete it" : ""}`}
                        >
                          <span className="mono">{d.index}</span>
                          <span>{d.chapter}</span>
                          <span className={l.draftTag}>{d.stale ? "stale" : "draft"}</span>
                        </Link>
                      ))}
                      {t.packs.length === 0 && !(t.drafts ?? []).length ? <span className={tableStyles.faint}>no packs yet</span> : null}
                    </span>
                  </td>
                  {canEdit ? (
                    <td data-align="right" className={l.generateCell}>
                      <Button size="sm" variant="secondary" onClick={() => setGenerating(t.id)} data-testid={`generate-${t.id}`} title={`Generate ${t.id} pack ${t.nextIndex ?? t.packs.length}`}>
                        Generate pack {t.nextIndex ?? t.packs.length}
                      </Button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={styles.footNote}>
        “Ahead” is how many published levels sit beyond the furthest player. Dashed packs are Studio drafts: players never see them until they are published.
      </p>
      <GenerateDialog track={generating} onClose={() => setGenerating(null)} />
    </>
  );
}

/** "Generate next pack": the engine's view of the track, then a levelpack job; its page shows the drafts as they land. */
function GenerateDialog({ track, onClose }: { track: string | null; onClose: () => void }) {
  const navigate = useNavigate();
  const generator = useLevelGenerator(track !== null);
  return (
    <Dialog open={track !== null} title={track ? `Generate the next ${track} pack` : "Generate level packs"} onClose={onClose} width={640}>
      {generator.isPending ? (
        <Loading rows={4} />
      ) : generator.isError ? (
        <StudioError error={generator.error} onRetry={() => void generator.refetch()} title="Can't reach the engine" />
      ) : (
        <LevelPackForm
          tracks={generator.data.tracks}
          initialTrack={track || null}
          lockTrack={Boolean(track)}
          onCancel={onClose}
          onQueued={(id) => {
            onClose();
            navigate(`/studio/jobs/${id}`);
          }}
        />
      )}
    </Dialog>
  );
}
